import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, lastValueFrom, Observable } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { CapacitorHttp, HttpOptions } from '@capacitor/core';
import { User } from '../app/models/user.model';
import { OktaAuthService } from './okta-auth.service';
import { UserProfileService } from './user-profile.service';
import { Injectable, InjectionToken } from '@angular/core';
import { environment } from '../environments/environment';
import { HybridStorageKeys, HybridStorageService } from '../app/services/hybrid-storage.service';
import { UserTypeEnum } from '../app/models/user-type.enum';
import { AuthStorageKeys, AuthStorageService } from '../app/services/auth-storage.service';
import { ProfileNotFoundError } from '../app/models/profile-not-found.error';
import BackgroundTokenRefresh from './background-token-refresh';
import { IdentitySwitchService } from './identity-switch.service';
import { loginDebugAlert } from './login-debug-alert';

export const USER_SERVICE_TOKEN = new InjectionToken<UserService>('UserService');

@Injectable({
  providedIn: 'root'
})
export class UserService {
  public userClaims: any;
  
  // BehaviorSubjects for reactive state management
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  private userClaimsSubject = new BehaviorSubject<any>(null);
  private currentUserProfileIdSubject = new BehaviorSubject<number | null>(null);
  private currentUserProfileSubject = new BehaviorSubject<User | null>(null);
  private impersonationChangeSubject = new BehaviorSubject<number>(0);

  // Internal state
  private _currentUserProfile?: User;
  private _originalUserProfile?: User; // Store original profile during impersonation
  private _isImpersonating: boolean = false;

  constructor(
    private oktaAuthService: OktaAuthService,
    private http: HttpClient,
    private userProfileService: UserProfileService,
    private hybridStorageService: HybridStorageService,
    private authStorageService: AuthStorageService,
    private identitySwitch: IdentitySwitchService
  ) {
    this.loadUserProfile();
  }

  // Load user profile from storage on service initialization
  private async loadUserProfile() {
    let userProfile: User | null = null;

    // On mobile: Load from secure storage (Keychain/EncryptedSharedPreferences)
    // On web: Load from localStorage via HybridStorage
    if (Capacitor.isNativePlatform()) {
      try {
        const result = await BackgroundTokenRefresh.getUserProfile();
        if (result.profile) {
          userProfile = JSON.parse(result.profile);
          
        } else {
          // Migration: Check old storage location (UserDefaults/SharedPreferences via HybridStorage)
          
          const oldProfile = await this.hybridStorageService.getItem<User>(
            HybridStorageKeys.UserService_CurrentUserProfile,
            false
          );

          if (oldProfile) {
          
            userProfile = oldProfile;

            // Migrate to new secure storage location
            try {
              await BackgroundTokenRefresh.saveUserProfile({
                profile: JSON.stringify(oldProfile)
              });
              

              // Optional: Clean up old storage location
              // await this.hybridStorageService.removeItem(HybridStorageKeys.UserService_CurrentUserProfile);
            } catch (migrationError) {
              console.error('[UserService] Migration failed, profile will remain in old location:', migrationError);
              // Profile still loaded in memory, user can continue
            }
          } else {
            console.warn('[UserService] No profile found in old or new storage');
          }
        }
      } catch (error) {
        console.error('[UserService] Failed to load profile from secure storage:', error);
      }
    } else {
      // Web platform - use HybridStorage (localStorage)
      userProfile = await this.hybridStorageService.getItem<User>(
        HybridStorageKeys.UserService_CurrentUserProfile,
        false  // Use localStorage - User object too large for cookies
      );
    }

    // Load impersonation state (always from HybridStorage for both web/mobile)
    const isImpersonating = await this.hybridStorageService.getItem<boolean>(
      HybridStorageKeys.UserService_IsImpersonating,
      false
    );

    const originalProfile = await this.hybridStorageService.getItem<User>(
      HybridStorageKeys.UserService_OriginalUserProfile,
      false  // Use localStorage - User object too large for cookies
    );

    if (userProfile) {
      try {
        this._currentUserProfile = userProfile;
        this._isImpersonating = isImpersonating || false;
        this._originalUserProfile = originalProfile || undefined;

        // Publish the Okta id from the restored profile. setUserProfile() is the only other source,
        // and it early-returns on several startup paths (no token yet, or a failed userinfo fetch
        // that falls back to this cached profile) — leaving anything gated on the id stuck off until
        // a manual reload. While impersonating, the authenticated user is the ORIGINAL profile; the
        // current one is the impersonated user and would report the wrong id.
        const authenticatedOktaUserId = this._originalUserProfile?.OktaUserId ?? userProfile.OktaUserId;
        if (authenticatedOktaUserId) {
          this.oktaAuthService.setOktaUserId(authenticatedOktaUserId);
        }

        this.currentUserProfileSubject.next(this._currentUserProfile || null);

        // Refresh IsServiceCenterStaff flag from API (in background)
        this.refreshServiceCenterStaffFlag();
      } catch (err) {
        console.error("Failed to parse stored UserProfile:", err);
      }
    }
  }

  // Refresh the IsServiceCenterStaff flag from the API
  private async refreshServiceCenterStaffFlag() {
    if (!this._currentUserProfile?.StaffProfile) return;

    // Wait for authentication to be ready before making API call
    const isAuthenticated = await this.isUserAuthenticated();
    if (!isAuthenticated) return;

    try {
      const isScStaff = await lastValueFrom(this.userProfileService.isServiceCenterStaff());
      if (this._currentUserProfile.StaffProfile.IsServiceCenterStaff !== isScStaff) {
        this._currentUserProfile.StaffProfile.IsServiceCenterStaff = isScStaff;
        await this.setCurrentUserProfile(this._currentUserProfile); // Triggers save to storage
      }
    } catch (error) {
      console.error('Error refreshing Service Center staff status:', error);
    }
  }

  // UserProfile getter and setter
  get CurrentUserProfile(): User | undefined {
    return this._currentUserProfile;
  }

  get IsImpersonating(): boolean {
    return this._isImpersonating;
  }

  get CurrentUserTimezonePrefs(): string {
    return this._currentUserProfile?.NotificationPrefs.Timezone ?? 'America/Detroit';
  }

  async setCurrentUserProfile(userProfile: User) {
      this._currentUserProfile = userProfile;

      // The client is now running as this identity, so the per-call-site `UserType === 'Guardian'`
      // guards are honest again and the blanket parent-portal hold from an in-progress account
      // switch can come down. No-op unless a switch raised it.
      this.identitySwitch.end();

      // On mobile: Save to secure storage (Keychain/EncryptedSharedPreferences)
      // On web: Save to localStorage via HybridStorage
      if (Capacitor.isNativePlatform()) {
        try {
          await BackgroundTokenRefresh.saveUserProfile({
            profile: JSON.stringify(userProfile)
          });
          
        } catch (error) {
          console.error('[UserService] Failed to save profile to secure storage:', error);
          // Fallback to HybridStorage on error
          await this.hybridStorageService.setItem<User>(
            HybridStorageKeys.UserService_CurrentUserProfile,
            userProfile,
            false
          );
        }
      } else {
        // Web platform - use HybridStorage (localStorage)
        await this.hybridStorageService.setItem<User>(
          HybridStorageKeys.UserService_CurrentUserProfile,
          userProfile,
          false  // Use localStorage - User object too large for cookies
        );
      }

      await this.hybridStorageService.setItem(
        HybridStorageKeys.MainMenu_UserFirstName,
        this.CurrentUserProfile?.FirstName || "");


      // Emit changes
      this.currentUserProfileSubject.next(userProfile);
      this.currentUserProfileIdSubject.next(userProfile?.Id || null);
    }

  // Impersonation methods
  async startImpersonation(impersonatedUserId: number): Promise<void> {
    if (!this._isImpersonating && this._currentUserProfile) {
      // Store the original profile
      this._originalUserProfile = { ...this._currentUserProfile };
      this._isImpersonating = true;

      try {
        // Fetch the impersonated user's profile from API
        const impersonatedProfile = await this.fetchUserProfileById(impersonatedUserId);
        await this.setCurrentUserProfile(impersonatedProfile);

        // Persist impersonation state to storage
        await this.hybridStorageService.setItem(
          HybridStorageKeys.UserService_IsImpersonating,
          true,
          false
        );
        await this.hybridStorageService.setItem(
          HybridStorageKeys.UserService_OriginalUserProfile,
          this._originalUserProfile,
          false  // Use localStorage - User object too large for cookies
        );

        this.impersonationChangeSubject.next(impersonatedUserId);

        // Clear caches after impersonation starts
        this.clearCachesForImpersonation();
      } catch (error) {
        console.error('Failed to start impersonation:', error);
        // Reset impersonation state on error
        this._isImpersonating = false;
        this._originalUserProfile = undefined;
        throw error;
      }
    }
  }

  async stopImpersonation(): Promise<void> {
    if (this._isImpersonating && this._originalUserProfile) {
      try {
        // Restore the original profile
        await this.setCurrentUserProfile(this._originalUserProfile);
        this._isImpersonating = false;
        this._originalUserProfile = undefined;

        // Clear persisted impersonation state from storage
        await this.hybridStorageService.removeItem(HybridStorageKeys.UserService_IsImpersonating);
        await this.hybridStorageService.removeItem(HybridStorageKeys.UserService_OriginalUserProfile);

        this.impersonationChangeSubject.next(0);

        // Clear caches after stopping impersonation
        this.clearCachesForImpersonation();
      } catch (error) {
        console.error('Failed to stop impersonation:', error);
        throw error;
      }
    }
  }

  // Get the authenticated user's profile directly from API (bypasses any cached impersonation)
  async getAuthenticatedUserProfile(): Promise<User> {
    await this.setUserProfile();
    // After setUserProfile completes, _currentUserProfile will contain the authenticated user
    // We need to return the non-impersonated profile
    if (this._isImpersonating && this._originalUserProfile) {
      return this._originalUserProfile;
    }
    return this._currentUserProfile!;
  }

  // Fetch user profile by ID (you'll need to implement this API call)
  private async fetchUserProfileById(userId: number): Promise<User> {
    // This method should call your API to get the user profile by ID
    // You'll need to implement this based on your API structure
    return lastValueFrom(
      this.userProfileService.getUserProfileByUserId(userId)
    );
  }

  // Global setUserProfile method to handle Okta and backend profile creation
  async setUserProfile(): Promise<void> {
    const oktaAuth = this.oktaAuthService.getOktaAuth();
    var token: any;

    await oktaAuth.tokenManager.get('accessToken').then(async function(accessToken) {
      if (accessToken && !oktaAuth.tokenManager.hasExpired(accessToken)) {
        // Token is valid
        token = accessToken;
      } 
    })
    .catch(function(err) {
      // handle OAuthError or AuthSdkError
      console.error(err);
      throw new Error(err.message);
    });

    if (this.userClaims || !token) {
      return;
    }

    // Get user info endpoint using the service method
    const userInfoEndpoint = this.oktaAuthService.getUserInfoEndpoint();

    // Check if the platform is native or web
    if (Capacitor.isNativePlatform()) {
      // Native platform: use CapacitorHttp
      const options: HttpOptions = {
        url: userInfoEndpoint,
        headers: {
          'Authorization': `Bearer ${(token as any).accessToken}`,
          'Content-Type': 'application/json'
        }
      };

      // Transient network blips and Okta 429/5xx are common right after returning from the
      // auth browser; a single-shot call turned those into a hard login failure. Retry the
      // idempotent userinfo GET a few times with backoff before giving up.
      let userInfoError: any = null;
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          const response = await CapacitorHttp.get(options);

          if (response.status === 200) {
            this.userClaims = response.data;
            userInfoError = null;
            break;
          }

          console.error('Failed to fetch user info:', response);
          userInfoError = new Error(`Failed to fetch user info: HTTP ${response.status}`);
          // Only transient server-side statuses are worth retrying; other 4xx (e.g. 401 bad
          // token) won't change on retry.
          if (response.status !== 429 && response.status < 500) {
            break;
          }
        } catch (error) {
          // Network/transport failure - retryable.
          userInfoError = error;
        }

        if (attempt < 3) {
          await new Promise(res => setTimeout(res, 500 * attempt));
        }
      }

      if (userInfoError) {
        console.error('[UserService] Failed to fetch user info from Okta (possibly offline):', userInfoError);
        // If we have a cached user profile, allow app to continue offline
        if (this._currentUserProfile) {
          return; // Exit early - skip backend import call
        }
        // No cached profile - rethrow error
        throw userInfoError;
      }
    } else {
      // Web platform: use Angular HttpClient
      const headers = new HttpHeaders({
        'Authorization': `Bearer ${(token as any).accessToken}`,
        'Content-Type': 'application/json'
      });

      // Same retry rationale as the native branch above.
      let userInfoError: any = null;
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          this.userClaims = await lastValueFrom(
            this.http.get<any>(userInfoEndpoint, { headers })
          );
          userInfoError = null;
          break;
        } catch (error: any) {
          userInfoError = error;
          const status = error?.status;
          // Non-transient 4xx (e.g. 401 bad token) won't change on retry.
          if (status && status !== 429 && status < 500) {
            break;
          }
        }

        if (attempt < 3) {
          await new Promise(res => setTimeout(res, 500 * attempt));
        }
      }

      if (userInfoError) {
        console.error('[UserService] Failed to fetch user info from Okta (possibly offline):', userInfoError);
        // If we have a cached user profile, allow app to continue offline
        if (this._currentUserProfile) {
          return; // Exit early - skip backend import call
        }
        // No cached profile - rethrow error
        throw userInfoError;
      }
    }

    loginDebugAlert('3) Got user info OK');

    // Build UserProfile object
    const userProfile = new User();
    userProfile.OktaUserId = this.userClaims['sub'];
    userProfile.Email = this.userClaims['email'];

    // Set OktaUserId in Okta Server
    this.oktaAuthService.setOktaUserId(this.userClaims['sub']);

    // Check email domain replacements
    userProfile.Email = userProfile.Email.toLowerCase().replace("@nhatest.com", "@nhaschools.com");
    userProfile.Email = userProfile.Email.toLowerCase().replace("@nhascholartest.com", "@nhascholar.com");

    // Set user type from login type selection before importing
    const loginType = this.authStorageService.getItem(AuthStorageKeys.Okta_LoginType);
    if (loginType === 'Staff') userProfile.UserTypeId = UserTypeEnum.Staff;
    else if (loginType === 'Student') userProfile.UserTypeId = UserTypeEnum.Student;
    else if (loginType === 'Parent') userProfile.UserTypeId = UserTypeEnum.Guardian;

    // Set UserGUID depingind on Okta Instance
    if (loginType === 'Parent') userProfile.UserGUID = this.userClaims['guardian_guid']
    else userProfile.UserGUID = this.userClaims['NHAGUID'];

    // Use UserProfileService to send the user profile to your backend with retry logic
    return new Promise<void>(async (resolve, reject) => {
      const maxRetries = 3;
      const retryDelay = 1000; // 1 second between retries

      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          const result = await lastValueFrom(this.userProfileService.importUserProfile(userProfile));

          if (!result) {
            throw new ProfileNotFoundError('The server did not return a profile for your account. Please contact your school administrator.');
          }

          // Check if user is Service Center staff and set the flag on StaffProfile
          if (result.StaffProfile) {
            try {
              const isScStaff = await lastValueFrom(this.userProfileService.isServiceCenterStaff());
              result.StaffProfile.IsServiceCenterStaff = isScStaff;
            } catch (error) {
              console.error('Error checking Service Center staff status:', error);
              result.StaffProfile.IsServiceCenterStaff = false;
            }
          }

          await this.setCurrentUserProfile(result);
          this.currentUserProfileSubject.next(result);
          this.userClaimsSubject.next(this.userClaims);
          this.emitAuthStatus();
          resolve(); // Success!
          return; // Exit retry loop
        } catch (error) {
          console.error(`[UserService] Error importing user profile from backend (attempt ${attempt}/${maxRetries}):`, error);

          // A 404 means the account authenticated but has no record to link to. That is
          // deterministic - retrying and falling back to a cached profile both just hide the
          // reason from the user, so surface it immediately.
          //
          // CapacitorHttp is enabled (capacitor.config.ts), so on native Angular's XHR is routed
          // through Capacitor's shim. Check the status structurally rather than with
          // `instanceof HttpErrorResponse` so this behaves the same in the app and on the web.
          if (error instanceof ProfileNotFoundError || (error as any)?.status === 404) {
            let message: string;

            if (error instanceof ProfileNotFoundError) {
              message = error.message;
            } else {
              // Body arrives parsed on web; the native shim can hand it back as a JSON string.
              let body = (error as any)?.error;
              if (typeof body === 'string') {
                try { body = JSON.parse(body); } catch { /* not JSON - fall through to default */ }
              }
              message = body?.Message ?? 'We could not find your account. Please contact your school administrator.';
            }

            console.error('[UserService] Backend has no profile to link this login to - not retrying:', message);
            reject(new ProfileNotFoundError(message));
            return;
          }

          // If this is not the last attempt, wait before retrying
          if (attempt < maxRetries) {
            
            await new Promise(res => setTimeout(res, retryDelay));
            continue;
          }

          // All retries exhausted - check for cached profile fallback
          if (this._currentUserProfile) {
            console.warn('[UserService] Backend import failed after retries but cached profile exists - allowing offline access');
            // Emit auth status to allow app to continue with cached data
            this.emitAuthStatus();
            resolve(); // Continue with cached profile
          } else {
            // No cached profile - cannot proceed
            console.error('[UserService] Backend import failed after retries and no cached profile available - rejecting');
            reject(error);
          }
        }
      }
    });
  }

  // Observable getters
  get userClaims$(): Observable<any> {
    return this.userClaimsSubject.asObservable();
  }

  get currentUserProfileId$(): Observable<number | null> {
    return this.currentUserProfileIdSubject.asObservable();
  }

  get currentUserProfile$(): Observable<User | null> {
    return this.currentUserProfileSubject.asObservable();
  }

  get impersonationChange$(): Observable<number> {
    return this.impersonationChangeSubject.asObservable();
  }

  get authStatus$(): Observable<boolean> {
    return this.isAuthenticatedSubject.asObservable();
  }

  // Emit authentication status based on `isUserAuthenticated`
  public async emitAuthStatus() {
    const isAuthenticated = await this.isUserAuthenticated();
    this.isAuthenticatedSubject.next(isAuthenticated);
  }

  // Emit auth status with a specific value (for offline mode with expired access token but valid refresh token)
  public emitAuthStatusValue(isAuthenticated: boolean) {
    this.isAuthenticatedSubject.next(isAuthenticated);
  }

  // Check if the user is authenticated
  async isUserAuthenticated(): Promise<boolean> {
    
    let oktaAuth = this.oktaAuthService.getOktaAuth();
    if (!oktaAuth || !oktaAuth.tokenManager) {
      await this.oktaAuthService.initialize();
      oktaAuth = this.oktaAuthService.getOktaAuth();
    }

    const accessToken = await oktaAuth.tokenManager.get('accessToken');
    return (accessToken != null && !oktaAuth.tokenManager.hasExpired(accessToken));
  }

  // Helper method to check if user name can be displayed
  async canDisplayUserName(isAuthenticated: boolean, userId: number, userClaims: any) {
    return isAuthenticated && userId > -1 && userClaims;
  }

  // Clear all user data
  public async clearAll() {
    this.hybridStorageService.clear();
    
    // Reset all internal state
    this._currentUserProfile = undefined;
    this._originalUserProfile = undefined;
    this._isImpersonating = false;
    this.userClaims = null;
    
    // Emit cleared state
    this.currentUserProfileSubject.next(null);
    this.currentUserProfileIdSubject.next(null);
    this.userClaimsSubject.next(null);
    this.isAuthenticatedSubject.next(false);
    this.impersonationChangeSubject.next(0);
  }

  /**
   * Clear all relevant caches during impersonation to prevent stale data
   */
  private clearCachesForImpersonation(): void {
    
    try {
      // Clear browser cache for current domain
      if ('caches' in window) {
        caches.keys().then(cacheNames => {
          cacheNames.forEach(cacheName => {
            caches.delete(cacheName);
          });
        });
      }
      
      // Clear localStorage items that might be user-specific
      const userSpecificKeys = [
        'mainmenu_items',
        'user_preferences',
        'cached_posts',
        'cached_messages'
      ];
      
      userSpecificKeys.forEach(async key => {
       await  this.hybridStorageService.removeItem(key);
      });
    } catch (error) {
      console.error('Error clearing caches during impersonation:', error);
    }
  }

  async getUserDeviceInfo(): Promise<any> {
    let deviceInfo: any;
    if(Capacitor.isNativePlatform()) {
      deviceInfo = await this.userProfileService.getUserCurrentMobileDeviceInformation();
      deviceInfo.isMobile = true;
    }
    else {
       // Get user agent info for web
      deviceInfo = {
        isMobile: false,
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        screen: {
          width: window.screen.width,
          height: window.screen.height
        }
      };
    }

    return deviceInfo;
  }

  async logUserDeviceInfo(formattedDeviceInfo: any): Promise<void>      
  {
    const userProfile = this.CurrentUserProfile;
    if (userProfile) {
      this.http.post(`${environment.apiUrl}/UserProfile/log-user-device-info`, {
        userId: userProfile.Id,
        isMobile: formattedDeviceInfo.isMobile,
        deviceInfo: formattedDeviceInfo
      }).subscribe({
        next: (_) => {},
        error: (error) => console.error('Error logging device info:', error)
      });
    }
  }

  updateShowAIAsync(isVisible: boolean): Observable<boolean> {
    return this.http.post<boolean>(`${environment.apiUrl}/UserProfile/UpdateShowAIAsync/${isVisible}`, {});
  }

  updateShowGuidedTourAsync(isVisible: boolean): Observable<boolean> {
    return this.http.post<boolean>(`${environment.apiUrl}/UserProfile/UpdateShowGuidedTourAsync/${isVisible}`, {});
  }

}
